import React, { useEffect, useState } from 'react';
import { Stack, TableBody, TableCell, TableRow, IconButton, Tooltip, Chip, Box } from '@mui/material';
import axios from 'axios';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { AddButton } from '../../../components/common/button/CustomButtons';
import ChildHeader from '../../../components/ui/ChildHeader';
import { SearchFilterContainer } from '../../../assets/styles/layout/searchFilter.styled';
import FilterBy from '../../../components/common/FilterBy';
import { SearchBar } from '../../../components/common/SearchBar';
import { CustomPagination, TableCustom } from '../../../components/common';
import useDebounce from '../../../hooks/useDebounce';
import {
  EditIconButton,
  DeleteIconButton,
} from '../../../components/common/button/CustomIconButton';
import Create from './Create';
import ModalCustom from '../../../components/common/Modal';
import { useQuery } from '../../../hooks/UseQuery';
import useConfirm from '../../../components/common/useConfirm';
import { IData } from './helpers/Interface';
import StageList from './StageList';
import { useAuth } from '../../../services/auth/AuthProvider';
import CustomUploadIcon from '../../../components/common/button/CustomUploadIcon';

// User IDs for access control
const ALLOWED_USER_IDS = [
  '6630b1321433a777c3031e7b', // Admin
  '6630ae31feb7b21c70b60d7e', // Quality Manager
  '6634ad002d0f294182dea256', // Rajesh
];

const tableHeaderObj = [
  [
    { text: 'S.NO.', rowspan: 1, colspan: 1 },
    { text: 'DEFECT NAME', rowspan: 1, colspan: 1 },
    { text: 'STAGE', rowspan: 1, colspan: 1 },
    { text: 'ATTACHMENTS', rowspan: 1, colspan: 1 },
    /*{ text: 'Product', rowspan: 1, colspan: 1 },
                                                            { text: 'Type Of Industry', rowspan: 1, colspan: 1 },
                                                            { text: 'Remarks', rowspan: 1, colspan: 1 },*/
    { text: 'ACTION', rowspan: 1, colspan: 1 },
  ],
];

const List = () => {
  const { state } = useAuth();
  const currentUserId = state.user?._id?.toString();
  const canEdit = currentUserId && ALLOWED_USER_IDS.includes(currentUserId);

  const [data, setData] = useState<IData[]>([]);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [render, setRender] = useState(false);
  const [defectId, setDefectId] = useState<string | null>(null);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState(0);
  const search = useQuery('search') || '';
  const filter = useQuery('filter') || '';
  const page = useQuery('page') || 1;
  const [isStageModal, setIsStageModal] = useState(false);
  const [stageData, setStageData] = useState([]);
  const [selectedDefect, setSelectedDefect] = useState<IData | null>(null);
  const [isAttachmentModal, setIsAttachmentModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string | null>(null);

  const [DialogDelete, confirmDelete] = useConfirm(
    'DELETE DEFECT',
    'Are you sure you want to delete this?',
  );

  const fetchData = async () => {
    const res = await axios
      .get(
        `/api/defectRoutes/?page=${page}&filter=${filter}&search=${search}&perPage=${perPage}`,
      )
      .catch((err) => {
        console.log(err);
      });
    setData(res?.data?.results);
    setPerPage(res?.data?.perPage);
    setTotalPages(res?.data?.pages);
  };

  useEffect(() => {
    axios
      .get('/api/defectRoutes/defectStage/defectStageForAutoComplete')
      .then((res) => {
        if (res.data) {
          const filterData = res.data.map((item: any) => ({
            name: item.name,
            value: item._id,
          }));
          setStageData(filterData);
        }
      })
      .catch((err) => console.log(err));
  }, []);
  const deleteDefect = async (id: string) => {
    const ans = await confirmDelete();
    if (!ans) return;
    await axios
      .delete(`/api/defectRoutes/${id}`)
      .then((res) => {
        alert('Document deleted successfully');
        setRender((prev) => !prev);
      })
      .catch((err) => console.log(err));
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    defectId: string
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(defectId);
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const response = await axios.post(
        `/api/defectRoutes/upload/${defectId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data) {
        alert('Files uploaded successfully');
        setRender((prev) => !prev);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploadingFiles(null);
      event.target.value = '';
    }
  };

  const handleViewAttachments = (defect: IData) => {
    setSelectedDefect(defect);
    setIsAttachmentModal(true);
  };

  const handleDeleteAttachment = async (defectId: string, attachmentId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this attachment?');
    if (!confirmed) return;

    try {
      await axios.delete(`/api/defectRoutes/attachment/${defectId}/${attachmentId}`);
      alert('Attachment deleted successfully');
      setRender((prev) => !prev);

      // Update selected defect if modal is open
      if (selectedDefect && selectedDefect._id === defectId) {
        const updatedDefect = await axios.get(`/api/defectRoutes/${defectId}`);
        setSelectedDefect(updatedDefect.data);
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      alert('Error deleting attachment');
    }
  };

  useDebounce(fetchData, 500, [page, search, render, isModal, filter]);

  return (
    <>
      <ChildHeader text="DEFECT LIST">
        {canEdit && (
          <>
            <AddButton onClick={() => setIsModal(true)} label="New Defect" />
            <AddButton onClick={() => setIsStageModal(true)} label="STAGE" />
          </>
        )}
      </ChildHeader>
      <SearchFilterContainer filter={true}>
        <SearchBar label="Search" />
        <FilterBy options={stageData} />
      </SearchFilterContainer>
      <TableCustom tableHeaderObj={tableHeaderObj}>
        <TableBody>
          {data &&
            data.map((item: IData, index: number) => (
              <React.Fragment key={item._id}>
                <TableRow>
                  <TableCell align="center">
                    {perPage * (+page - 1) + index + 1}
                  </TableCell>
                  <TableCell align="center">{item?.defect_name}</TableCell>
                  <TableCell
                    align="center"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {item?.stage_id?.name}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                      {canEdit && (
                        <>
                          <input
                            id={`file-upload-${item._id}`}
                            type="file"
                            multiple
                            accept="image/*,.pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileUpload(e, item._id || '')}
                            disabled={uploadingFiles === item._id}
                          />
                          <label htmlFor={`file-upload-${item._id}`}>
                            <Tooltip title={uploadingFiles === item._id ? 'Uploading...' : 'Upload Photos/PDFs'}>
                              <IconButton component="span" disabled={uploadingFiles === item._id}>
                                <AttachFileIcon color={uploadingFiles === item._id ? 'disabled' : 'primary'} />
                              </IconButton>
                            </Tooltip>
                          </label>
                        </>
                      )}
                      {item?.attachments && item.attachments.length > 0 && (
                        <>
                          <Chip
                            label={item.attachments.length}
                            size="small"
                            color="primary"
                          />
                          <Tooltip title="View Attachments">
                            <IconButton onClick={() => handleViewAttachments(item)} size="small">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" sx={{ justifyContent: 'center' }}>
                      {canEdit && (
                        <EditIconButton
                          tooltipTitle="EDIT"
                          onClick={() => {
                            setDefectId(item?._id || '');
                            setIsModal(true);
                          }}
                        />
                      )}

                      {canEdit && (
                        <DeleteIconButton
                          tooltipTitle="DELETE"
                          onClick={() => {
                            deleteDefect(item?._id || '');
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
        </TableBody>
      </TableCustom>
      <CustomPagination totalPage={totalPages} />
      <ModalCustom
        openModal={isModal}
        closeModal={() => {
          setDefectId(null);
          setIsModal(false);
        }}
        title="DEFECT"
      >
        <Create id={defectId} setIsModal={setIsModal} />
      </ModalCustom>
      <ModalCustom
        openModal={isStageModal}
        closeModal={() => {
          setIsStageModal(false);
        }}
        title="STAGE LIST"
      >
        <StageList />
      </ModalCustom>
      <ModalCustom
        openModal={isAttachmentModal}
        closeModal={() => {
          setIsAttachmentModal(false);
          setSelectedDefect(null);
        }}
        title={`ATTACHMENTS - ${selectedDefect?.defect_name || ''}`}
      >
        <Box sx={{ p: 2 }}>
          {selectedDefect?.attachments && selectedDefect.attachments.length > 0 ? (
            <Stack spacing={2}>
              {selectedDefect.attachments.map((attachment, idx) => (
                <Box
                  key={attachment._id || idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                    {attachment.fileType?.startsWith('image') ? (
                      <img
                        src={attachment.url}
                        alt={attachment.filename}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <AttachFileIcon sx={{ fontSize: 40, color: '#d32f2f' }} />
                    )}
                    <Box>
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: '#1976d2' }}
                      >
                        {attachment.filename}
                      </a>
                      <Box sx={{ fontSize: 12, color: '#666', mt: 0.5 }}>
                        {attachment.fileType} • {new Date(attachment.uploadedAt || '').toLocaleDateString()}
                      </Box>
                    </Box>
                  </Stack>
                  {canEdit && (
                    <DeleteIconButton
                      tooltipTitle="Delete"
                      onClick={() =>
                        handleDeleteAttachment(
                          selectedDefect._id || '',
                          attachment._id || ''
                        )
                      }
                    />
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3, color: '#666' }}>
              No attachments available
            </Box>
          )}
        </Box>
      </ModalCustom>
      <DialogDelete isSubmitContent={false} />
    </>
  );
};

export default List;
